import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Building2, 
  Wrench, 
  Package, 
  Mail, 
  Send, 
  Loader2, 
  CheckCircle, 
  ArrowLeft,
  User,
  Building
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/constants/branding";

export default function PublicRegisterPage() {
  const [searchParams] = useSearchParams();
  const portalType = searchParams.get("type") || "hospital";
  
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const portalConfig = {
    hospital: {
      title: "Hospital Staff Registration",
      description: "Request access to your facility's operational dashboard",
      icon: Building2,
      color: "cyan",
      bgGradient: "from-cyan-600 to-blue-700",
      loginPath: "/hospital/login",
      loginText: "Hospital Staff Login"
    },
    contractor: {
      title: "Contractor Registration",
      description: "Join the AskIsaac Marketplace as a service contractor",
      icon: Wrench,
      color: "violet",
      bgGradient: "from-violet-600 to-purple-700",
      loginPath: "/contractor/login",
      loginText: "Contractor Login"
    },
    supplier: {
      title: "Supplier Registration",
      description: "Partner with us to supply healthcare facilities",
      icon: Package,
      color: "amber",
      bgGradient: "from-amber-600 to-orange-700",
      loginPath: "/supplier/login",
      loginText: "Supplier Login"
    }
  };

  const config = portalConfig[portalType] || portalConfig.hospital;
  const Icon = config.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/public/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          portal_type: portalType,
          name: name.trim() || undefined,
          company_name: companyName.trim() || undefined
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        toast.success("Setup link sent to your email!");
      } else {
        toast.error(data.detail || "Registration failed. Please try again.");
      }
    } catch (err) {
      console.error("Registration error:", err);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">Check Your Email!</h1>
            <p className="text-slate-300 mb-6">
              We've sent a setup link to <span className="text-white font-medium">{email}</span>
            </p>
            
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-400">
                Click the link in the email to complete your registration and set up your account. 
                The link will expire in 7 days.
              </p>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-slate-400">Didn't receive the email?</p>
              <Button 
                variant="outline" 
                onClick={() => { setSuccess(false); setEmail(""); }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Try Again
              </Button>
            </div>
            
            <div className="mt-8 pt-6 border-t border-slate-700">
              <Link 
                to={config.loginPath}
                className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to {config.loginText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="AskIsaac" className="h-12 mx-auto mb-4" />
          </Link>
        </div>

        {/* Registration Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${config.bgGradient} p-6 text-center`}>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">{config.title}</h1>
            <p className="text-white/80 text-sm mt-1">{config.description}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4" />
                Email Address *
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Your Name
              </Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="register-name"
              />
            </div>

            {(portalType === "contractor" || portalType === "supplier") && (
              <div>
                <Label className="text-slate-300 flex items-center gap-2 mb-2">
                  <Building className="w-4 h-4" />
                  Company Name
                </Label>
                <Input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company LLC"
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="register-company"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r ${config.bgGradient} hover:opacity-90 text-white font-medium py-5`}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              Request Setup Link
            </Button>

            <p className="text-xs text-slate-500 text-center">
              By registering, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="border-t border-slate-700 pt-4">
              <p className="text-center text-sm text-slate-400">
                Already have an account?{" "}
                <Link to={config.loginPath} className={`text-${config.color}-400 hover:underline font-medium`}>
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Portal Switcher */}
        <div className="mt-6 flex justify-center gap-4">
          {Object.entries(portalConfig).map(([type, cfg]) => {
            const IconComponent = cfg.icon;
            const isActive = type === portalType;
            return (
              <Link
                key={type}
                to={`/register?type=${type}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive 
                    ? `bg-${cfg.color}-500/20 text-${cfg.color}-400 border border-${cfg.color}-500/50` 
                    : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
                }`}
              >
                <IconComponent className="w-4 h-4" />
                <span className="text-sm capitalize">{type}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
